/* =========================================================================
   May 20 — custom revamp behavior
   - Scroll-reveal for [data-reveal] / [data-reveal-stagger]
   - <scroll-showcase> split-scroll centerpiece controller
   Mirrors Horizon's own IntersectionObserver pattern (see jumbo-text.js).
   No third-party libraries. Respects prefers-reduced-motion.
   ========================================================================= */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ---------- 1. Scroll-reveal ---------------------------------------------- */
(function initReveal() {
  const targets = document.querySelectorAll('[data-reveal], [data-reveal-stagger]');
  if (!targets.length) return;

  // Reduced motion: the hidden state only exists inside a
  // (prefers-reduced-motion: no-preference) block, so content is already
  // visible. Nothing to do.
  if (reducedMotion.matches) return;

  const observer = new IntersectionObserver(
    (entries, obs) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target); // one-time
        }
      }
    },
    { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
  );

  targets.forEach((el) => observer.observe(el));
})();

/* ---------- 2. Split-scroll showcase -------------------------------------- */
class ScrollShowcase extends HTMLElement {
  connectedCallback() {
    this.panels = Array.from(this.querySelectorAll('.scroll-showcase__panel'));
    this.dots = Array.from(this.querySelectorAll('.scroll-showcase__dot'));
    this.count = this.panels.length;
    this.activeIndex = -1;
    this.ticking = false;

    if (!this.count) return;

    this.style.setProperty('--ss-count', String(this.count));

    this.desktop = window.matchMedia('(min-width: 750px)');
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);

    this.enable();
    this.onResize(); // set initial state

    this.desktop.addEventListener('change', this.onResize);
    window.addEventListener('resize', this.onResize, { passive: true });
  }

  disconnectedCallback() {
    this.disable();
    this.desktop?.removeEventListener('change', this.onResize);
    window.removeEventListener('resize', this.onResize);
  }

  get pinned() {
    return this.desktop.matches && !reducedMotion.matches;
  }

  enable() {
    if (this._listening) return;
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this._listening = true;
  }

  disable() {
    if (!this._listening) return;
    window.removeEventListener('scroll', this.onScroll);
    this._listening = false;
  }

  onResize() {
    if (this.pinned) {
      this.enable();
      this.update();
    } else {
      // Fallback mode: CSS shows every panel stacked. Clear JS state.
      this.disable();
      this.panels.forEach((p) => p.classList.remove('is-active'));
      this.dots.forEach((d) => d.classList.remove('is-active'));
      this.activeIndex = -1;
    }
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  }

  update() {
    if (!this.pinned) return;
    const rect = this.getBoundingClientRect();
    const total = rect.height - window.innerHeight;
    if (total <= 0) return;
    const progress = Math.min(Math.max(-rect.top / total, 0), 1);
    // Map progress across `count` equal segments.
    let index = Math.floor(progress * this.count);
    if (index >= this.count) index = this.count - 1;
    if (index < 0) index = 0;
    this.setActive(index);
  }

  setActive(index) {
    if (index === this.activeIndex) return;
    this.activeIndex = index;
    this.panels.forEach((panel, i) => {
      panel.classList.toggle('is-active', i === index);
    });
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
    });
  }
}

if (!customElements.get('scroll-showcase')) {
  customElements.define('scroll-showcase', ScrollShowcase);
}
